import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import {
  cancelImport,
  resumeImport,
  resolveImportImage,
  resolveImportIssue,
  getDashboard,
  getImport,
  getImportEvents,
  getImportIssues,
  listImports,
  startImport,
  startA2Backfill,
  getA2BackfillStatus,
  getA2BackfillEvents,
  type A2BackfillStatus,
  type DashboardPayload,
  type Import,
  type ImportEvent,
  type ImportImage,
  type ImportIssue,
  type ImportOptions,
} from '../lib/api';
import { sanitise } from '../lib/evessio/sanitise';
import { LoadingScreen } from '../components/LoadingScreen';
import { useAuth } from '../contexts/AuthContext';
import { WAF_YELLOW_DARK } from '../theme';

const primaryButtonSx = {
  backgroundColor: WAF_YELLOW_DARK,
  color: 'text.primary',
  fontWeight: 600,
  '&:hover': { backgroundColor: '#E6B800' },
  '&.Mui-disabled': { backgroundColor: '#F5D028', color: 'rgba(0,0,0,0.38)' },
};

function importStatusColor(
  status: Import['status']
): 'default' | 'success' | 'error' | 'warning' | 'info' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'cancelled':
      return 'warning';
    case 'running':
    case 'pending':
      return 'info';
    default:
      return 'default';
  }
}

async function readJsonFile(file: File): Promise<unknown> {
  const text = await file.text();
  return JSON.parse(text);
}

function fmtEventTime(iso: string): string {
  // Show only HH:MM:SS — the date is implicit (this is "live now") and
  // every line gets a timestamp so vertical alignment matters more than
  // precision.
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function ImportLiveLog({ events }: { events: ImportEvent[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Auto-scroll to the latest line whenever new events arrive. Skipping
  // smooth-scroll keeps the tail responsive when 8 workers are firing
  // events in parallel.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [events]);

  return (
    <Box
      ref={scrollRef}
      sx={(t) => ({
        height: 200,
        overflowY: 'auto',
        backgroundColor: t.palette.mode === 'dark' ? '#0F1115' : '#1A1D23',
        color: '#D1D5DB',
        fontFamily: '"JetBrains Mono", "Fira Code", Menlo, Consolas, monospace',
        fontSize: 12,
        lineHeight: 1.5,
        borderRadius: 1,
        p: 1.5,
        whiteSpace: 'pre-wrap',
      })}
    >
      {events.length === 0 ? (
        <Box component="span" sx={{ opacity: 0.5 }}>Waiting for activity…</Box>
      ) : (
        events.map((e) => (
          <Box key={e.sequence} component="div">
            <Box component="span" sx={{ color: '#6B7280', mr: 1 }}>{fmtEventTime(e.timestamp)}</Box>
            <Box component="span">{e.message}</Box>
          </Box>
        ))
      )}
    </Box>
  );
}

function fmtBytes(n: number | null | undefined): string {
  if (!n) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

// A selectable card for one past import. Clicking it loads that import's
// detail (issues, failed images) below.
function ImportTile({
  imp,
  active,
  onClick,
}: {
  imp: Import;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={(t) => ({
        cursor: 'pointer',
        width: 230,
        p: 2,
        borderRadius: 2,
        border: `2px solid ${active ? WAF_YELLOW_DARK : t.palette.divider}`,
        backgroundColor: active ? 'rgba(255,204,0,0.08)' : 'background.paper',
        transition: 'border-color 120ms, background-color 120ms',
        '&:hover': { borderColor: WAF_YELLOW_DARK },
      })}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Import #{imp.id}
        </Typography>
        <Chip size="small" label={imp.status} color={importStatusColor(imp.status)} />
      </Stack>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
        {new Date(imp.created_at).toLocaleString()}
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        {imp.images_done}/{imp.images_total} images
        {imp.images_failed > 0 && (
          <Box component="span" sx={{ color: 'error.main', ml: 1 }}>
            · {imp.images_failed} failed
          </Box>
        )}
      </Typography>
    </Box>
  );
}

export function Import() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('Admin');

  const [nominationsFile, setNominationsFile] = useState<File | null>(null);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [maxWidth, setMaxWidth] = useState(2540);
  const [qualityLarge, setQualityLarge] = useState(70);
  const [concurrency, setConcurrency] = useState(3);
  const [limit, setLimit] = useState<number | ''>('');

  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Whether the "start new import" form is open. Collapsed by default so the
  // screen opens on the import picker (tiles), not the upload form.
  const [showForm, setShowForm] = useState(false);
  const [listLoaded, setListLoaded] = useState(false);

  const [activeImportId, setActiveImportId] = useState<number | null>(null);
  // Bumped by Resume to restart the polling effect for the same import id.
  const [pollNonce, setPollNonce] = useState(0);
  const [activeImport, setActiveImport] = useState<Import | null>(null);
  const [activeImages, setActiveImages] = useState<ImportImage[]>([]);
  const [activeIssues, setActiveIssues] = useState<ImportIssue[]>([]);
  const [activeEvents, setActiveEvents] = useState<ImportEvent[]>([]);
  const [recentImports, setRecentImports] = useState<Import[]>([]);
  const [imageIssues, setImageIssues] = useState<DashboardPayload | null>(null);

  // A2 board backfill (admin-only tool). Kicks a background job on the backend
  // and polls its status + live event tail — same mechanism as the import above.
  const [a2Status, setA2Status] = useState<A2BackfillStatus | null>(null);
  const [a2Events, setA2Events] = useState<ImportEvent[]>([]);
  const [a2Error, setA2Error] = useState<string | null>(null);
  const a2LastSeq = useRef(-1);
  const a2Polling = useRef(false);

  const pollA2 = useCallback(() => {
    if (a2Polling.current) return; // one poll loop at a time
    a2Polling.current = true;
    const tick = async () => {
      try {
        const [status, events] = await Promise.all([
          getA2BackfillStatus(),
          getA2BackfillEvents(a2LastSeq.current >= 0 ? a2LastSeq.current : undefined).catch(
            () => [] as ImportEvent[]
          ),
        ]);
        setA2Status(status);
        if (events.length > 0) {
          a2LastSeq.current = events[events.length - 1].sequence;
          setA2Events((prev) => [...prev, ...events].slice(-200));
        }
        if (status.state === 'running') {
          setTimeout(tick, 1000);
        } else {
          a2Polling.current = false;
        }
      } catch {
        a2Polling.current = false;
      }
    };
    tick();
  }, []);

  const refreshList = () =>
    listImports()
      .then(setRecentImports)
      .catch(() => {
        /* ignore */
      });

  useEffect(() => {
    listImports()
      .then((r) => {
        setRecentImports(r);
        setListLoaded(true);
      })
      .catch(() => setListLoaded(true));
    getDashboard().then(setImageIssues).catch(() => { /* ignore */ });
  }, []);

  // Load the backfill's last/current status (admin only). If a run is already in
  // flight — e.g. another admin started it, or we navigated away and back —
  // pick the live tail back up.
  useEffect(() => {
    if (!isAdmin) return;
    getA2BackfillStatus()
      .then((s) => {
        setA2Status(s);
        if (s.state === 'running') pollA2();
      })
      .catch(() => { /* ignore */ });
  }, [isAdmin, pollA2]);

  const onStartA2Backfill = async () => {
    const ok = window.confirm(
      'Backfill A2 boards?\n\nThis downloads every entry’s external A2 board PDF to our storage and ' +
        'rewrites its link to our copy. It only touches A2 boards — nothing else. Safe to re-run: ' +
        'boards already stored are skipped.'
    );
    if (!ok) return;
    setA2Error(null);
    setA2Events([]);
    a2LastSeq.current = -1;
    try {
      const s = await startA2Backfill();
      setA2Status(s);
      pollA2();
    } catch (err) {
      setA2Error(err instanceof Error ? err.message : 'failed to start A2 backfill');
    }
  };

  useEffect(() => {
    if (!activeImportId) return;
    let cancelled = false;
    setActiveEvents([]);
    // Track the highest sequence we've appended so the next /events poll
    // returns only the new lines. Reset when the import id changes.
    let lastSeq = -1;
    const tick = async () => {
      try {
        const [{ import: imp, images }, issues, events] = await Promise.all([
          getImport(activeImportId),
          getImportIssues(activeImportId).catch(() => [] as ImportIssue[]),
          getImportEvents(activeImportId, lastSeq >= 0 ? lastSeq : undefined)
            .catch(() => [] as ImportEvent[]),
        ]);
        if (cancelled) return;
        setActiveImport(imp);
        setActiveImages(images);
        setActiveIssues(issues);
        if (events.length > 0) {
          lastSeq = events[events.length - 1].sequence;
          // Keep at most 200 in state so a long-running import's log doesn't
          // grow unbounded in the React tree.
          setActiveEvents((prev) => [...prev, ...events].slice(-200));
        }
        if (imp.status === 'running' || imp.status === 'pending') {
          // Faster cadence than the existing 1.5s — matches the live-tail UX.
          setTimeout(tick, 1000);
        } else {
          // Import has finished (completed/failed/cancelled) — refresh the
          // recent-imports list AND the image-issues panel, both of which
          // reflect post-import state and would otherwise stay stale.
          refreshList();
          getDashboard().then(setImageIssues).catch(() => { /* ignore */ });
        }
      } catch {
        if (!cancelled) setTimeout(tick, 3000);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [activeImportId, pollNonce]);

  const onCancel = async () => {
    if (!activeImportId) return;
    try {
      await cancelImport(activeImportId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'cancel failed');
    }
  };

  const onResume = async () => {
    if (!activeImportId) return;
    setError(null);
    try {
      // Reuse the form's concurrency as the override, so you can drop it
      // (e.g. 8 -> 2 after an OOM) before retrying.
      await resumeImport(activeImportId, concurrency);
      setPollNonce((n) => n + 1); // restart polling for the now-running import
    } catch (err) {
      setError(err instanceof Error ? err.message : 'resume failed');
    }
  };

  const onResolveIssue = async (issue: ImportIssue, resolved: boolean) => {
    try {
      const r = await resolveImportIssue(activeImportId!, issue.id, resolved);
      setActiveIssues((prev) =>
        prev.map((i) =>
          i.id === issue.id ? { ...i, resolved_at: r.resolved_at, resolved_by: r.resolved_by } : i
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to update issue');
    }
  };

  const onResolveImage = async (img: ImportImage, resolved: boolean) => {
    try {
      const r = await resolveImportImage(activeImportId!, img.id, resolved);
      setActiveImages((prev) =>
        prev.map((i) =>
          i.id === img.id ? { ...i, resolved_at: r.resolved_at, resolved_by: r.resolved_by } : i
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to update image');
    }
  };

  const onStart = async () => {
    if (!nominationsFile) {
      setError('Nominations JSON file is required');
      return;
    }
    setError(null);
    setStarting(true);
    try {
      const rawNoms = await readJsonFile(nominationsFile);
      // Sanitise in the browser so PII (raw judging notes, scoring data,
      // anything beyond the whitelisted onboarding fields) never reaches
      // the server.
      const { entries } = sanitise(rawNoms);
      if (entries.length === 0) {
        throw new Error('no nominations after sanitising — check the input file');
      }
      const options: ImportOptions = {
        year,
        max_width: maxWidth,
        quality_when_large: qualityLarge,
        concurrency,
        limit: typeof limit === 'number' && limit > 0 ? limit : undefined,
      };
      const { importId } = await startImport(entries, options);
      setShowForm(false);
      setActiveImportId(importId);
      refreshList();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to start import');
    } finally {
      setStarting(false);
    }
  };

  const totalBytesSaved = activeImages.reduce((sum, i) => {
    if (i.bytes_in && i.bytes_out) return sum + (i.bytes_in - i.bytes_out);
    return sum;
  }, 0);
  const failedImages = activeImages.filter((i) => i.status === 'failed');

  const progressPct = activeImport && activeImport.images_total > 0
    ? Math.round(((activeImport.images_done + activeImport.images_failed) / activeImport.images_total) * 100)
    : 0;

  // Small reusable resolve control: a checkbox + (when resolved) the admin
  // who dealt with it. Used on both onboarding issues and failed images.
  const resolveControl = (
    resolved: boolean,
    resolvedBy: string | null,
    onToggle: (next: boolean) => void
  ) => (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
      <Tooltip title={resolved ? `Resolved by ${resolvedBy ?? 'an admin'}` : 'Mark resolved'}>
        <Checkbox
          size="small"
          checked={resolved}
          onChange={(e) => onToggle(e.target.checked)}
        />
      </Tooltip>
      {resolved && resolvedBy && (
        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={resolvedBy}>
          {resolvedBy}
        </Typography>
      )}
    </Stack>
  );

  return (
    <Stack spacing={3}>
      <Typography variant="h3" component="h1" align="center">
        Evessio import
      </Typography>

      {/* Import picker — pick a past import to inspect, or start a new one. */}
      {!listLoaded ? (
        <LoadingScreen />
      ) : (
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h5">Imports</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={primaryButtonSx}
              onClick={() => {
                setShowForm(true);
                setActiveImportId(null);
                setActiveImport(null);
              }}
            >
              Start new import
            </Button>
          </Stack>
          {recentImports.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No imports yet — start your first import to bring in entries and images.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {recentImports.map((j) => (
                <ImportTile
                  key={j.id}
                  imp={j}
                  active={j.id === activeImportId}
                  onClick={() => {
                    setShowForm(false);
                    setActiveImportId(j.id);
                  }}
                />
              ))}
            </Box>
          )}
        </Paper>
      )}

      {/* Admin-only A2 board backfill. Re-hosts external Evessio A2 board PDFs on
          our storage without needing cluster access to run the Seeder command. */}
      {isAdmin && (
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              useFlexGap
              spacing={1}
            >
              <Box sx={{ maxWidth: 640 }}>
                <Typography variant="h5">A2 board backfill</Typography>
                <Typography variant="body2" color="text.secondary">
                  Downloads every entry’s external A2 board PDF onto our own storage and rewrites
                  its link to our copy. Only touches A2 boards — no images or other fields are
                  changed. Idempotent: safe to re-run, boards already stored are skipped.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<CloudDownloadOutlinedIcon />}
                sx={primaryButtonSx}
                disabled={a2Status?.state === 'running'}
                onClick={onStartA2Backfill}
              >
                {a2Status?.state === 'running' ? 'Backfilling…' : 'Backfill A2 boards'}
              </Button>
            </Stack>

            {a2Error && <Alert severity="error">{a2Error}</Alert>}

            {a2Status && a2Status.state !== 'idle' && (
              <>
                <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                  <Typography variant="body2">
                    <strong>Status:</strong> {a2Status.state}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Scanned:</strong> {a2Status.scanned}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Downloaded:</strong> {a2Status.downloaded}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Skipped:</strong> {a2Status.skipped}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: a2Status.failed > 0 ? 'error.main' : undefined }}
                  >
                    <strong>Failed:</strong> {a2Status.failed}
                  </Typography>
                </Stack>
                {a2Status.error && <Alert severity="error">{a2Status.error}</Alert>}
                {a2Status.state === 'running' && <ImportLiveLog events={a2Events} />}
              </>
            )}
          </Stack>
        </Paper>
      )}

      {/* New-import form — collapsed until "Start new import" is clicked. */}
      {showForm && (
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h5">1. Upload Evessio nominations JSON</Typography>
            <Typography variant="body2" color="text.secondary">
              The file is sanitised in the browser before upload — all PII is removed on the front end and never reaches our server.
              Email addresses are stored and used to create accounts.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileOutlinedIcon />}
                sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 320 } }}
              >
                Nominations JSON {nominationsFile ? `✓ ${nominationsFile.name}` : '(required)'}
                <input
                  type="file"
                  hidden
                  accept="application/json,.json"
                  onChange={(e) => setNominationsFile(e.target.files?.[0] ?? null)}
                />
              </Button>
            </Stack>
            <Typography variant="h5" sx={{ pt: 1 }}>2. Compression settings</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
              <TextField
                label="Year"
                size="small"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                sx={{ width: { xs: '100%', sm: 120 } }}
              />
              <TextField
                label="Max width (px)"
                type="number"
                size="small"
                value={maxWidth}
                onChange={(e) => setMaxWidth(Number(e.target.value))}
                sx={{ width: { xs: '100%', sm: 160 } }}
              />
              <TextField
                label="Quality (large raws)"
                type="number"
                size="small"
                value={qualityLarge}
                onChange={(e) => setQualityLarge(Number(e.target.value))}
                sx={{ width: { xs: '100%', sm: 200 } }}
              />
              <TextField
                label="Concurrency"
                select
                size="small"
                value={concurrency}
                onChange={(e) => setConcurrency(Number(e.target.value))}
                sx={{ width: { xs: '100%', sm: 160 } }}
              >
                {[1, 2, 3, 4, 6, 8].map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Limit entries"
                type="number"
                size="small"
                placeholder="all"
                value={limit}
                onChange={(e) => {
                  const v = e.target.value;
                  setLimit(v === '' ? '' : Math.max(0, Number(v)));
                }}
                sx={{ width: { xs: '100%', sm: 160 } }}
                helperText="blank = all"
              />
            </Stack>

            {error && <Alert severity="error">{error}</Alert>}

            <Box>
              <Button
                variant="contained"
                onClick={onStart}
                disabled={starting || !nominationsFile}
                startIcon={<PlayArrowIcon />}
                sx={primaryButtonSx}
              >
                {starting ? 'Uploading…' : 'Start import'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      {activeImport && (
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              useFlexGap
              spacing={1}
            >
              <Typography variant="h5">Import #{activeImport.id} — {activeImport.status}</Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" color="text.secondary">
                  {activeImport.images_done} done · {activeImport.images_failed} failed · {activeImport.images_total} total
                </Typography>
                {(activeImport.status === 'pending' || activeImport.status === 'running') && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<StopCircleOutlinedIcon />}
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                )}
                {(activeImport.status === 'failed' || activeImport.status === 'cancelled') &&
                  (activeImport.images_failed > 0 ||
                    activeImport.images_done + activeImport.images_failed < activeImport.images_total) && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<ReplayOutlinedIcon />}
                      onClick={onResume}
                      sx={primaryButtonSx}
                    >
                      Resume / retry failed
                    </Button>
                  )}
              </Stack>
            </Stack>
            <LinearProgress variant="determinate" value={progressPct} sx={{ height: 8, borderRadius: 4 }} />
            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
              <Typography variant="body2"><strong>Entries:</strong> {activeImport.entries_count}</Typography>
              <Typography variant="body2"><strong>Concurrency:</strong> {activeImport.concurrency}</Typography>
              <Typography variant="body2"><strong>Bytes saved:</strong> {fmtBytes(totalBytesSaved)}</Typography>
            </Stack>
            {activeImport.error_message && (
              <Alert severity="error">{activeImport.error_message}</Alert>
            )}
            {/* Live log only carries content while the import is running (the
                event buffer is in-memory). Hide it for finished imports. */}
            {(activeImport.status === 'running' || activeImport.status === 'pending') && (
              <ImportLiveLog events={activeEvents} />
            )}
            {activeIssues.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1 }}>
                  {activeIssues.length} onboarding issue{activeIssues.length === 1 ? '' : 's'}
                </Typography>
                <Stack spacing={0.5}>
                  {activeIssues.map((iss) => {
                    const resolved = !!iss.resolved_at;
                    return (
                      <Stack
                        key={iss.id}
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={{ xs: 0.5, sm: 2 }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ fontSize: 13, opacity: resolved ? 0.55 : 1 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', minWidth: { sm: 200 }, flexShrink: 0 }}
                          title={iss.kind}
                        >
                          {iss.kind}
                        </Typography>
                        <Typography variant="body2" sx={{ flex: { sm: 1 }, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {iss.message}
                        </Typography>
                        {iss.nomination_uuid && (
                          <Button
                            size="small"
                            variant="outlined"
                            component={RouterLink}
                          to={`/entry/${iss.nomination_uuid}`}
                          >
                            Open entry
                          </Button>
                        )}
                        {resolveControl(resolved, iss.resolved_by, (next) => onResolveIssue(iss, next))}
                      </Stack>
                    );
                  })}
                </Stack>
              </Paper>
            )}
            {failedImages.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                  {failedImages.length} image{failedImages.length === 1 ? '' : 's'} failed
                </Typography>
                <Stack spacing={1}>
                  {failedImages.map((img) => {
                    const resolved = !!img.resolved_at;
                    return (
                      <Stack
                        key={img.id}
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ fontSize: 13, opacity: resolved ? 0.55 : 1 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', minWidth: 64, flexShrink: 0 }}
                        >
                          {img.image_type}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ minWidth: { sm: 200 }, maxWidth: '100%', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={`${img.project_title ?? ''} (${img.nomination_code ?? img.nomination_uuid})`}
                        >
                          {img.project_title || img.nomination_code || img.nomination_uuid}
                        </Typography>
                        <Typography variant="body2" sx={{ flex: { sm: 1 }, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }} title={img.error_message ?? ''}>
                          {img.error_message || 'unknown error'}
                        </Typography>
                        {img.nomination_uuid && (
                          <Button
                            size="small"
                            variant="outlined"
                            component={RouterLink}
                          to={`/entry/${img.nomination_uuid}`}
                          >
                            Open entry
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<OpenInNewOutlinedIcon />}
                          href={img.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open raw
                        </Button>
                        {resolveControl(resolved, img.resolved_by, (next) => onResolveImage(img, next))}
                      </Stack>
                    );
                  })}
                </Stack>
              </Paper>
            )}
          </Stack>
        </Paper>
      )}

      {imageIssues && (
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              useFlexGap
              spacing={2}
            >
              <Typography variant="h5">Image upload issues</Typography>
              <Stack direction="row" spacing={3}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: imageIssues.images.failed > 0 ? 'error.main' : 'text.primary' }}>
                    {imageIssues.images.failed}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">unresolved</Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {imageIssues.images.recovered}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">recovered after retry</Typography>
                </Box>
              </Stack>
            </Stack>
            {imageIssues.recent_failures.length > 0 ? (
              <Stack spacing={0.5}>
                {imageIssues.recent_failures.map((f) => (
                  <Stack
                    key={f.id}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 0.5, sm: 2 }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ fontSize: 13, py: 0.5, borderBottom: (t) => `1px solid ${t.palette.divider}` }}
                  >
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', minWidth: 90 }}>
                      {f.image_type}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ flex: { sm: 1 }, minWidth: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      component={RouterLink}
                      to={`/entry/${f.nomination_uuid}`}
                    >
                      {f.project_title || f.nomination_code || f.nomination_uuid}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ flex: { sm: 1 }, minWidth: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={f.error_message ?? ''}
                    >
                      {f.error_message || 'unknown error'}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<OpenInNewOutlinedIcon />}
                      href={f.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open raw
                    </Button>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No unresolved image failures.
              </Typography>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
